/**
 * Visual constants shared by the field builder, WebGL material and export
 * compositor.
 *
 * Every surface colour here is a *physical albedo* authored in Oklch, not a
 * screen colour: the renderer computes light transport, so a stop states how
 * much light the material reflects. The poster chrome below is the opposite —
 * ink composited over the finished photograph, never lit, so it stays hex.
 */

import { oklchToHex, type Oklch } from "./oklab.ts";

/**
 * The table the plate rests on: warm rag paper, reflectance 0.79. Real uncoated
 * paper sits between 0.75 and 0.82. The old `#fbf1ee` was 0.93, brighter than
 * any diffuse material, which under image-based lighting can only read as chalk.
 */
export const PAPER: Oklch = [0.925, 0.012, 62];

/** The cast board of the plate, a shade cooler and brighter than the table. */
export const PLATE_ALBEDO: Oklch = [0.938, 0.008, 320];

/** Poster overlay ink, composited after the photograph. Never lit. */
export const BACKGROUND = oklchToHex(PAPER);
export const TEXT = "#2e2833";
export const MUTED = "#9b8d86";
export const LABEL = "#514c5a";
export const LEADER = "#918a9c";

/**
 * Warm plaster to dark stone: lightness 0.900 down to 0.455, i.e. reflectance
 * 0.73 down to 0.09, the range of a real gypsum casting.
 */
export const TERRAIN_PALETTE_STOPS: ReadonlyArray<readonly [number, Oklch]> = [
  [0.0, [0.9, 0.02, 80]],
  [0.22, [0.845, 0.028, 76]],
  [0.48, [0.76, 0.038, 68]],
  [0.72, [0.65, 0.04, 60]],
  [0.9, [0.545, 0.032, 52]],
  [1.0, [0.455, 0.024, 45]],
];

/**
 * Quiet lavender countryside through hot metropolitan cores: reflectance 0.65
 * down to 0.027. Chroma at the two magenta stops sits just inside the sRGB
 * boundary for that lightness and hue — pushing further clips a channel
 * negative, and a negative albedo destroys energy in the indirect bounce.
 */
export const POPULATION_PALETTE_STOPS: ReadonlyArray<readonly [number, Oklch]> = [
  [0.0, [0.87, 0.03, 295]],
  [0.12, [0.81, 0.055, 296]],
  [0.3, [0.7, 0.095, 298]],
  [0.48, [0.59, 0.135, 305]],
  [0.64, [0.5, 0.17, 330]],
  [0.78, [0.44, 0.178, 355]],
  [0.89, [0.395, 0.156, 8]],
  [0.96, [0.345, 0.13, 12]],
  [1.0, [0.31, 0.09, 16]],
];

/**
 * The sun, as the two angles a person can reason about. It used to be a frozen
 * vector because a baked hillshade and a marched shadow field were computed from
 * it once, in the worker, and could not be recomputed without redoing the whole
 * pipeline. Now it drives a `DirectionalLight` and the studio's key card, so it is
 * a control.
 *
 * Azimuth is measured from north, turning the way the camera presets do, so 133°
 * puts the light in the north-west; elevation is degrees above the horizon.
 */
export const SUN_AZIMUTH_DEG = 133;
export const SUN_ELEVATION_DEG = 52;

export function sunDirection(
  azimuthDeg: number,
  elevationDeg: number,
): [number, number, number] {
  const azimuth = (azimuthDeg * Math.PI) / 180;
  const elevation = (elevationDeg * Math.PI) / 180;
  const ground = Math.cos(elevation);
  return [-Math.sin(azimuth) * ground, -Math.cos(azimuth) * ground, Math.sin(elevation)];
}

/** Where the light sits by default; every shading stage agrees on it. */
export const LIGHT: readonly [number, number, number] = sunDirection(
  SUN_AZIMUTH_DEG,
  SUN_ELEVATION_DEG,
);

/**
 * Radiometry of the studio, in arbitrary but mutually consistent units. The
 * key-to-environment ratio sets the contrast; `EXPOSURE` then places the result
 * on the tone curve. Both are irradiance, so doubling either is a stop.
 */
export const KEY_INTENSITY = 2.6;
export const ENVIRONMENT_INTENSITY = 1.0;
export const EXPOSURE = 1.15;

/**
 * The physical referent, stated once: a sealed gypsum-powder casting standing
 * on a printed board, on rag paper. Roughness is high and specular reflectance
 * modest, but neither is zero — a surface with no specular lobe anywhere is the
 * most reliable single sign that an image was not photographed.
 */
export const RELIEF_ROUGHNESS_RURAL = 0.84;
export const RELIEF_ROUGHNESS_URBAN = 0.56;
export const RELIEF_SPECULAR = 0.42;
/** Short-range subsurface transport through thin spires: gypsum's signature. */
export const RELIEF_TRANSLUCENCY = 0.22;
/** Micro-relief of the cast surface: grid cells per grain, and its slope. */
export const GRAIN_CELLS = 1.6;
export const GRAIN_SLOPE = 0.42;
/**
 * How deep the horizon sweep looks for lateral support, in normalised units. It
 * is the depth over which gypsum stops being translucent: a spire narrower than
 * this glows at the edge, ground thicker than it does not.
 */
export const TRANSLUCENCY_DEPTH = 0.006;

/**
 * Weight of the single indirect bounce. Restrained on purpose: one bounce with no
 * visibility term of its own overestimates, and the magenta cores are exactly
 * where an overestimate would show as a glow.
 */
export const BOUNCE_STRENGTH = 0.42;

export const PLATE_ROUGHNESS = 0.44;
export const PLATE_CLEARCOAT = 0.3;
export const PLATE_CLEARCOAT_ROUGHNESS = 0.32;
export const PAPER_ROUGHNESS = 0.9;
export const PAPER_SHEEN = 0.25;

/**
 * Optical finish, every term applied in linear light before the tone curve —
 * which is the whole point: the old CSS vignette multiplied *encoded* pixels,
 * so it darkened without ever behaving like falling illumination.
 *
 * `APERTURE` is a circle-of-confusion radius as a fraction of frame height at
 * full defocus, not an f-number: the scene is unit-scaled, so an f-number would
 * mean nothing. It is measured against the subject's own depth, so it states how
 * much of the object stays sharp and reads the same from every preset.
 */
export const APERTURE = 0.0095;
export const VIGNETTE_STRENGTH = 0.34;
export const CHROMATIC_ABERRATION = 0.0024;
export const BARREL_DISTORTION = -0.007;
export const GLARE_THRESHOLD = 1.1;
export const GLARE_STRENGTH = 0.06;
export const GRAIN_AMOUNT = 0.016;

/** Jittered samples accumulated once the camera comes to rest. */
export const TAA_SAMPLES = 64;

/**
 * Needle height curve. The exponent is steep on purpose: in the reference maps
 * the rural fur is roughly a sixth of the tallest metro spike, so mid densities
 * must be compressed far harder than the urban tail.
 */
export const NEEDLE_GAIN = 0.52;
export const NEEDLE_EXPONENT = 6.2;

/**
 * A broader base survives overview-scale aggregation without turning each
 * populated cell into a hairline.
 */
export const CONE_SLOPE = 17.0;

/**
 * Vertical exaggeration applied to real elevation, and the share of the frame
 * the highest summits may occupy so small countries do not turn into walls.
 * Geometry stays gentle on purpose: a continuous surface exaggerated hard turns
 * every camera-facing slope into a curtain.
 */
export const TERRAIN_EXAGGERATION = 11.0;
export const TERRAIN_MAX_HEIGHT = 0.045;

/**
 * Population geometry stays subordinate to the country silhouette. Fine
 * density still lives in colour and reappears geometrically as the LOD rises.
 */
export const VERTICAL_SCALE = 0.145;

/**
 * Board thickness. Deliberately thick enough to read as an object: at the old
 * 0.006 it was four per cent of the spike height and simply never appeared in a
 * finished picture. Its underside is the plane the paper sits on, so the two
 * touch rather than one floating above the other.
 */
export const PLATE_DEPTH = 0.024;
/** Rounding of the top rim, where the specular highlight line lives. */
export const PLATE_FILLET = 0.0022;

/** Atlas and relief presets: the first is calmer, the second more spatial. */
export const CAMERAS = {
  top: { azimuthDeg: 0, elevationDeg: 50 },
  perspective: { azimuthDeg: 52, elevationDeg: 32 },
} as const;

/** Typography. The browser resolves the same families the Python build used. */
export const TITLE_FONT = '"Didot", "Bodoni 72", "Playfair Display", Georgia, serif';
export const SANS_FONT = '"Helvetica Neue", "Neue Haas Grotesk Text Pro", Arial, sans-serif';

/** Upstream data sources, all proxied and cached by the Bun server. */
export const WORLDPOP_URL =
  "https://data.worldpop.org/GIS/Population/Global_2000_2020/{year}/{iso3}/{iso3Lower}_ppp_{year}.tif";
export const GEOBOUNDARIES_API = "https://www.geoboundaries.org/api/current/gbOpen/{iso3}/ADM0/";
export const GEONAMES_URL = "https://download.geonames.org/export/dump/{iso2}.zip";
export const TERRAIN_URL = "https://elevation-tiles-prod.s3.amazonaws.com/geotiff/{z}/{x}/{y}.tif";

export const TERRAIN_NODATA = -32768;
/** 512 px tiles, so equatorial ground resolution is 78271.5 / 2**zoom metres. */
export const TERRAIN_TILE_RESOLUTION = 78_271.5;
export const TERRAIN_MAX_TILES = 150;

/**
 * Target grid: about 110 m per cell, capped so a large country stays inside a
 * sane memory budget while a tiny one is not needlessly upsampled.
 */
export const GRID_TARGET_METRES = 110;
export const GRID_MAX_PIXELS = 3200;
export const GRID_MIN_PIXELS = 256;
